import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { searchAPI } from '../lib/api';
import { Input } from './ui/input';
import { Search, Package, Truck, Warehouse, Loader2, X, Pencil, FileText, Briefcase } from 'lucide-react';
import { EditItemModal } from './EditItemModal';
import { AllocateToJobModal } from './AllocateToJobModal';
import { useAuth } from '../contexts/AuthContext';

export const GlobalSearch = ({ autoFocus = false, onClose = null, dark = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner, hasPerm } = useAuth();
  const canAssignJob = isOwner || hasPerm('jobs.parts.assign');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  
  // Quick action modals state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [allocatingItem, setAllocatingItem] = useState(null);

  // Auto focus for mobile overlay
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Close dropdown on route change
  useEffect(() => {
    setIsOpen(false);
    setQuery('');
    setResults(null);
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Debounced search with AbortController
  const performSearch = useCallback(async (searchQuery, requestId) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (searchQuery.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const response = await searchAPI.search(searchQuery, { signal: controller.signal });
      
      // Only update if this is still the most recent request
      if (requestId === requestIdRef.current && !controller.signal.aborted) {
        setResults(response.data);
        setSelectedIndex(-1);
        setIsOpen(true);
      }
    } catch (error) {
      // Ignore abort errors - these are intentional cancellations
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Search failed:', error);
      if (requestId === requestIdRef.current) {
        setResults(null);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Handle query changes with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      // Increment request ID to track latest request
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      
      setLoading(true); // Show loading immediately
      debounceRef.current = setTimeout(() => {
        performSearch(query, currentRequestId);
      }, 300);
    } else {
      setResults(null);
      setLoading(false);
      if (query.length === 0) {
        setIsOpen(false);
      }
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build flat list of results for keyboard navigation
  const flatResults = React.useMemo(() => {
    const list = [];
    if (results) {
      results.items.forEach((item) => {
        list.push({ type: 'item', data: item });
      });
      results.stock_locations.forEach((loc) => {
        list.push({ type: 'location', data: loc });
      });
    }
    return list;
  }, [results]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || flatResults.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
        if (onClose) onClose();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = Math.min(prev + 1, flatResults.length - 1);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = Math.max(prev - 1, -1);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
          handleResultClick(flatResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        if (onClose) onClose();
        break;
      default:
        break;
    }
  };

  const handleResultClick = (result) => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setIsOpen(false);
    setQuery('');
    setResults(null);
    if (onClose) onClose();

    if (result.type === 'item') {
      navigate('/inventory', { state: { highlightItem: result.data.item_id } });
    } else if (result.type === 'location') {
      const loc = result.data;
      if (loc.location_type === 'warehouse') {
        navigate('/warehouse', { state: { highlightItem: loc.item_id } });
      } else if (loc.location_type === 'fleet') {
        navigate('/inventory', { state: { showFleet: true, highlightItem: loc.item_id } });
      } else {
        navigate(`/vans/${loc.vehicle_id}`, { state: { highlightItem: loc.item_id } });
      }
    }
  };

  const clearSearch = () => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setQuery('');
    setResults(null);
    setIsOpen(false);
    setLoading(false);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (value.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (query.length >= 2 && results) {
      setIsOpen(true);
    }
  };

  // Quick action handlers for item results
  const handleEditItem = (item, e) => {
    e.stopPropagation();
    setEditingItem(item);
    setEditModalOpen(true);
    // Don't close search dropdown - user might want to continue searching
  };

  const handleCreatePO = (item, e) => {
    e.stopPropagation();
    // Close search and navigate to PO page with prefill state
    setIsOpen(false);
    setQuery('');
    setResults(null);
    if (onClose) onClose();
    
    // Store prefill data in sessionStorage (cleared after use by PurchaseOrders page)
    const prefillData = {
      line_items: [{
        item_id: item.item_id,
        item_name: item.item_name,
        quantity: 1,
        unit_cost: item.unit_cost || ''
      }]
    };
    sessionStorage.setItem('prefill_po', JSON.stringify(prefillData));
    
    // Navigate to PO page with prefill flag
    navigate('/purchase-orders?prefill=true');
  };

  const handleAllocateToJob = (item, e) => {
    e.stopPropagation();
    setAllocatingItem(item);
    setAllocateModalOpen(true);
    // Don't close search dropdown - user might want to continue searching
  };

  // Callback when edit modal saves successfully
  const handleEditSuccess = () => {
    // Could refresh search results here if needed
    // For now, just close the modal - user can search again
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${dark ? 'text-white/60' : 'text-muted-foreground'}`} />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search items, SKUs..."
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className={`pl-9 pr-8 h-9 ${dark ? 'bg-white/15 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/30' : 'bg-muted/50 border-muted focus:bg-background'}`}
        />
        {(query || loading) && (
          <button
            onClick={clearSearch}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${dark ? 'hover:bg-white/10' : 'hover:bg-muted'}`}
            type="button"
          >
            {loading ? (
              <Loader2 className={`w-4 h-4 animate-spin ${dark ? 'text-white/60' : 'text-muted-foreground'}`} />
            ) : (
              <X className={`w-4 h-4 ${dark ? 'text-white/60' : 'text-muted-foreground'}`} />
            )}
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {loading && !results ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : !results || (results.items.length === 0 && results.stock_locations.length === 0) ? (
            <div className="py-8 text-center text-muted-foreground">
              No results for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <div className="py-2">
              {/* Items Section */}
              {results.items.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase">
                    Inventory Items
                  </div>
                  {results.items.map((item, idx) => {
                    const flatIdx = idx;
                    return (
                      <div
                        key={`item-${item.item_id}`}
                        className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-muted transition-colors group ${
                          selectedIndex === flatIdx ? 'bg-muted' : ''
                        }`}
                      >
                        <button
                          onClick={() => handleResultClick({ type: 'item', data: item })}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          type="button"
                          data-testid={`search-result-item-${item.item_id}`}
                        >
                          <Package className="w-4 h-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.item_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.sku && <span className="font-mono">{item.sku}</span>}
                              {item.sku && item.category_name && ' · '}
                              {item.category_name}
                            </p>
                          </div>
                        </button>
                        {/* Quick Action Buttons - compact, inline on right */}
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button
                            type="button"
                            onClick={(e) => handleEditItem(item, e)}
                            className="p-1.5 rounded hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                            title="Edit item"
                            data-testid={`search-edit-${item.item_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleCreatePO(item, e)}
                            className="p-1.5 rounded hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                            title="Create PO"
                            data-testid={`search-create-po-${item.item_id}`}
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                          {canAssignJob && (
                          <button
                            type="button"
                            onClick={(e) => handleAllocateToJob(item, e)}
                            className="p-1.5 rounded hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                            title="Allocate to Job"
                            data-testid={`search-allocate-${item.item_id}`}
                          >
                            <Briefcase className="w-3.5 h-3.5" />
                          </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Stock Locations Section */}
              {results.stock_locations.length > 0 && (
                <div>
                  <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase mt-2 border-t pt-2">
                    Stock Locations
                  </div>
                  {results.stock_locations.map((loc, idx) => {
                    const flatIdx = results.items.length + idx;
                    const isWarehouse = loc.location_type === 'warehouse';
                    const isFleet = loc.location_type === 'fleet';
                    return (
                      <div
                        key={`loc-${loc.item_id}-${loc.vehicle_id || loc.location_id || 'fleet'}-${idx}`}
                        className={`w-full px-3 py-2 flex items-center gap-3 hover:bg-muted transition-colors group ${
                          selectedIndex === flatIdx ? 'bg-muted' : ''
                        }`}
                      >
                        <button
                          onClick={() => handleResultClick({ type: 'location', data: loc })}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          type="button"
                        >
                          {isWarehouse ? (
                            <Warehouse className="w-4 h-4 text-primary shrink-0" />
                          ) : isFleet ? (
                            <Warehouse className="w-4 h-4 text-muted-foreground shrink-0" />
                          ) : (
                            <Truck className="w-4 h-4 text-muted-foreground shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{loc.item_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {isWarehouse ? loc.location_name : isFleet ? 'Fleet Inventory' : loc.vehicle_name}
                              {loc.location_label && ` · ${loc.location_label}`}
                              {loc.assigned_tech_name && ` · ${loc.assigned_tech_name}`}
                            </p>
                          </div>
                          <div className="text-sm font-mono font-bold text-foreground">
                            {loc.quantity}
                          </div>
                        </button>
                        {/* Quick Action: Allocate to Job (warehouse only, permission-gated) */}
                        {isWarehouse && canAssignJob && (
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            <button
                              type="button"
                              onClick={(e) => handleAllocateToJob({
                                item_id: loc.item_id,
                                name: loc.item_name,
                                item_name: loc.item_name,
                                sku: loc.sku,
                                // Pre-select this warehouse
                                preselect_warehouse_id: loc.location_id
                              }, e)}
                              className="p-1.5 rounded hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                              title="Allocate to Job"
                              data-testid={`search-allocate-loc-${loc.item_id}`}
                            >
                              <Briefcase className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Edit Item Modal */}
      <EditItemModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        item={editingItem}
        onSuccess={handleEditSuccess}
      />

      {/* Allocate to Job Modal */}
      <AllocateToJobModal
        open={allocateModalOpen}
        onOpenChange={setAllocateModalOpen}
        item={allocatingItem}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
